import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Modal, OverlayTrigger, Row, Table, Tooltip } from 'react-bootstrap';
import { IUser } from '../../../model/user/IUser';
import { SupervisorsService } from '../SupervisorService';
import SupervisorFilter from './SupervisorFilter';
import { ImpersonateEye } from '../../../shared/components/ImpersonateEye';
import { ErrorToast, SuccessToast } from '../../../utils/toasters';
import TableLayout from '../../../shared/layout/table/TableLayout';
import { ITableColumn } from '../../../shared/components/TableSkeleton';

const SUPERVISOR_COLUMNS: ITableColumn[] = [
    { label: 'ID' },
    { label: 'User' },
    { label: 'Email' },
    { label: 'Calendly' },
    { label: 'Campaigns', className: 'text-center' },
];

export default function SupervisorsList() {
    const [dataLoading, setDataLoading] = useState<boolean>(true);
    const [state, setState] = useState({
        page: 1,
        perPage: 15,
        totalPages: 0,
    });
    const [supervisors, setSupervisors] = useState<IUser[]>([]);
    const [triggerSupervisor, setTriggerSupervisor] = useState<boolean>(false);
    const [showModal, setShowModal] = useState<{ show: boolean; user: Partial<IUser> }>({
        show: false,
        user: {},
    });
    const [editData, setEditData] = useState({
        supervisorLink: '',
    });

    useEffect(() => {
        setDataLoading(true);
        SupervisorsService.getSupervisors(state)
            .then(data => {
                setSupervisors(data.data);
                setState(prev => ({ ...prev, totalPages: Math.ceil(data.count / state.perPage) }));
            })
            .catch(err => ErrorToast(err))
            .finally(() => setDataLoading(false));
    }, [state.page, triggerSupervisor]);

    const handlePages = (updatePage: number) => {
        setState({ ...state, page: updatePage });
    };

    const handleChange = (e: { target: HTMLInputElement }, isEditCalendly?: boolean) => {
        const { name, value } = e.target;
        if (!isEditCalendly) {
            setState(prev => ({ ...prev, [name]: value }));
        } else {
            setEditData(prev => ({ ...prev, [name]: value }));
        }
    };

    const saveEditCalendly = () => {
        SupervisorsService.putCalendly(showModal?.user?.id as number, editData)
            .then(() => {
                SuccessToast('Successfully action');
                setTriggerSupervisor(!triggerSupervisor);
            })
            .catch(err => ErrorToast(err));
    };

    return (
        <div>
            <Modal
                show={showModal.show}
                onHide={() => setShowModal({ ...showModal, show: false })}
                size="lg"
                aria-labelledby="contained-modal-title-vcenter"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title id="contained-modal-title-vcenter">Edit Calendly</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row>
                        <Form.Group as={Col}>
                            <Form.Control
                                className="form-control autocomplete-input mb-lg-0"
                                placeholder="Calendly"
                                type="text"
                                value={editData.supervisorLink}
                                name="supervisorLink"
                                onChange={(e: any) => handleChange(e, true)}
                            />
                        </Form.Group>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-primary" onClick={() => setShowModal({ ...showModal, show: false })}>
                        Close
                    </Button>
                    <Button
                        onClick={() => {
                            saveEditCalendly();
                            setShowModal({ ...showModal, show: false });
                        }}
                    >
                        Save
                    </Button>
                </Modal.Footer>
            </Modal>
            <SupervisorFilter
                handleChange={handleChange}
                setTriggerSupervisor={setTriggerSupervisor}
                setParams={setState}
            />

            <TableLayout
                pagination={state}
                listLength={supervisors.length}
                handlePages={handlePages}
                isLoading={dataLoading}
                dataName={'supervisors'}
                skeletonColumns={SUPERVISOR_COLUMNS}
                skeletonRows={state.perPage}
            >
                <Table hover responsive>
                    <thead>
                        <tr>
                            {SUPERVISOR_COLUMNS.map(column => (
                                <th key={column.label} className={column.className}>
                                    {column.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {supervisors?.map(item => (
                            <tr key={item.id}>
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip id={`tooltip-${item.id}`}>
                                            {!item.isEnabled ? 'Disabled' : item.isSuspended ? 'Suspended' : 'Active'}
                                        </Tooltip>
                                    }
                                >
                                    <td
                                        className={
                                            !item.isEnabled ? 'text-danger' : item.isSuspended ? 'text-warning' : ''
                                        }
                                    >
                                        {item.id}
                                    </td>
                                </OverlayTrigger>
                                <td>
                                    {item.firstName} {item.lastName}
                                </td>
                                <td>
                                    <ImpersonateEye id={item.id ?? 0} />
                                    {item.email}
                                </td>
                                <td>
                                    <Button
                                        className="mr-2"
                                        onClick={() => {
                                            setEditData({ supervisorLink: item.supervisorLink || '' });
                                            setShowModal({ show: true, user: item });
                                        }}
                                    >
                                        Edit
                                    </Button>
                                    {!!item.supervisorLink && (
                                        <a target="_blank" href={item.supervisorLink} rel="noreferrer">
                                            Calendly link
                                        </a>
                                    )}
                                </td>
                                <td className="text-center">{item.nbSupervisedCampaigns}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </TableLayout>
        </div>
    );
}
